import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
} from '@mui/material';
import GroupTypeRoleSelector from './GroupTypeRoleSelector';
import { useGroupTypeNameMap } from '../hooks/useGroupTypeNameMap';
import {
  Audience,
  AudienceCriteria,
  buildAudienceCriteria,
  estimateEmailCampaignAudience,
  hasAudienceSource,
} from '../endpoints/emailCampaigns';
import { GroupTypeRoleSelection } from '../types/group';

export interface EmailCampaignAudienceFormProps {
  criteria: GroupTypeRoleSelection[];
  onCriteriaChange: (next: GroupTypeRoleSelection[]) => void;
  legacyAudienceIds: string[];
  onLegacyAudienceIdsChange: (ids: string[]) => void;
  activeMembersOnly: boolean;
  onActiveMembersOnlyChange: (value: boolean) => void;
  legacyAudiences: Audience[];
  estimateCount: number | null;
  onEstimateCountChange: (count: number | null) => void;
  onPreview: (criteria: AudienceCriteria) => void;
}

const EmailCampaignAudienceForm: React.FC<EmailCampaignAudienceFormProps> = ({
  criteria,
  onCriteriaChange,
  legacyAudienceIds,
  onLegacyAudienceIdsChange,
  activeMembersOnly,
  onActiveMembersOnlyChange,
  legacyAudiences,
  estimateCount,
  onEstimateCountChange,
  onPreview,
}) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [estimating, setEstimating] = useState(false);

  const { groupNameById } = useGroupTypeNameMap('email-campaign-audience');

  const audienceCriteria = useMemo(
    () => buildAudienceCriteria(criteria, activeMembersOnly, legacyAudienceIds),
    [criteria, activeMembersOnly, legacyAudienceIds]
  );

  const toggleLegacy = (id: string, checked: boolean) => {
    if (checked) {
      onLegacyAudienceIdsChange([...legacyAudienceIds, id]);
    } else {
      onLegacyAudienceIdsChange(legacyAudienceIds.filter((x) => x !== id));
    }
    onEstimateCountChange(null);
  };

  const handleApplyAudience = async () => {
    if (!hasAudienceSource(audienceCriteria)) return;
    setEstimating(true);
    try {
      const res = await estimateEmailCampaignAudience(audienceCriteria);
      onEstimateCountChange(res.count);
    } catch {
      onEstimateCountChange(null);
    } finally {
      setEstimating(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Audience
      </Typography>
      <Button
        variant="outlined"
        onClick={() => setSelectorOpen(true)}
        sx={{ mb: 1 }}
      >
        Select group types &amp; roles
      </Button>
      {criteria.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {[...criteria]
            .sort((a, b) =>
              (
                groupNameById.get(a.group_type_id) || a.group_type_id
              ).localeCompare(
                groupNameById.get(b.group_type_id) || b.group_type_id,
                undefined,
                { sensitivity: 'base' }
              )
            )
            .map((c) => (
              <Chip
                key={c.group_type_id}
                label={groupNameById.get(c.group_type_id) || c.group_type_id}
                onDelete={() =>
                  onCriteriaChange(
                    criteria.filter((x) => x.group_type_id !== c.group_type_id)
                  )
                }
                size="small"
              />
            ))}
        </Box>
      )}
      {legacyAudiences.length > 0 && (
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
          Preset audiences
        </Typography>
      )}
      {legacyAudiences.map((a) => (
        <FormControlLabel
          key={a.id}
          control={
            <Checkbox
              checked={legacyAudienceIds.includes(a.id)}
              onChange={(e) => toggleLegacy(a.id, e.target.checked)}
            />
          }
          label={a.name}
        />
      ))}
      <FormControlLabel
        control={
          <Checkbox
            checked={activeMembersOnly}
            onChange={(e) => {
              onActiveMembersOnlyChange(e.target.checked);
              onEstimateCountChange(null);
            }}
          />
        }
        label="Active members only"
      />
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          mt: 1,
          alignItems: 'center',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleApplyAudience}
          disabled={!hasAudienceSource(audienceCriteria) || estimating}
        >
          {estimating ? 'Estimating…' : 'Apply audience'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onPreview(audienceCriteria)}
          disabled={!hasAudienceSource(audienceCriteria)}
        >
          Preview recipients
        </Button>
        {estimateCount != null && (
          <Typography variant="body2" color="text.secondary">
            Estimated recipients: {estimateCount}
          </Typography>
        )}
      </Box>
      <GroupTypeRoleSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSave={(next) => {
          onCriteriaChange(next);
          onEstimateCountChange(null);
        }}
        initialValue={criteria}
      />
    </Box>
  );
};

export default EmailCampaignAudienceForm;
